import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  HStack,
  Checkbox,
  VStack,
  Divider,
  Box,
} from '@chakra-ui/react';
import { Tag } from 'Atoms';
import { Typography } from 'Tokens';
import { useState, useRef, useEffect, useMemo } from 'react';
import { HelpTooltip } from '../HelpTooltip';
import { DataCollectionLevel } from '../../containers/Esrs/pieces/DataCollection';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  ListRowProps,
  ListRowRenderer,
  List as VirtualizedList,
} from 'react-virtualized';
import { SearchInput } from 'Molecules/SearchInput';

export const showWarningForNotAggregatable = (option: string) => {
  if (
    (option && option === DataCollectionLevel.subsidiaries) ||
    option === DataCollectionLevel.reportingUnits
  ) {
    return true;
  }
  return false;
};

const ChoicesDisplay = ({ choices }: { choices: string[] | undefined }) => {
  const [displayText, setDisplayText] = useState('Select');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (choices?.length) {
      const container = containerRef.current;
      if (!container) return;

      const formatText = (arr: string[], limit: number) => {
        let text = arr.slice(0, limit).join(', ');
        if (arr.length > limit) {
          text += `, +${arr.length - limit}`;
        }
        return text;
      };

      let currentText = formatText(choices, choices.length);
      container.innerText = currentText;

      if (container.scrollWidth <= container.clientWidth) {
        setDisplayText(currentText);
        return;
      }

      for (let i = choices.length - 1; i >= 0; i--) {
        currentText = formatText(choices, i);
        container.innerText = currentText;
        if (container.scrollWidth <= container.clientWidth) {
          setDisplayText(currentText);
          break;
        }
      }
    } else {
      setDisplayText('Select');
    }
  }, [choices]);

  return (
    <HStack spacing="4px">
      <Box
        ref={containerRef}
        width="calc(100%-20px)"
        maxW="480px"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {displayText}
      </Box>
      <ChevronDownIcon color="text.muted" boxSize="16px" />
    </HStack>
  );
};

export const MenuOptionsList = ({
  options,
  isOptionDisabled = () => false,
  isSingleSelect,
  selectedChoices,
  setSelectedChoices,
  handleChange,
  isNonAggregatable,
  isWarningDisplayed,
  notAggregatableWarningText,
}: {
  options: { value: string; label: string }[];
  isOptionDisabled?: (val: string) => boolean;
  isSingleSelect: boolean;
  selectedChoices: string[] | undefined;
  setSelectedChoices: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  handleChange: (val: string[] | undefined) => void;
  isNonAggregatable?: boolean;
  isWarningDisplayed: (option: string) => boolean;
  notAggregatableWarningText?: string;
}) => {
  return ({ key, index, style }: { key: string; index: number; style: React.CSSProperties }) => {
    const option = options[index];
    if (!option) return null;
    return (
      <div key={key} style={style}>
        <MenuItem
          isDisabled={isOptionDisabled(option.value)}
          flexDirection={'column'}
          onClick={(e) => {
            e.stopPropagation();
            if (isSingleSelect) {
              handleChange(selectedChoices?.includes(option.value) ? undefined : [option.value]);
            } else {
              setSelectedChoices(
                (selectedChoices?.includes(option.value) ?? false)
                  ? selectedChoices?.filter((c) => c !== option.value)
                  : [...(selectedChoices ?? []), option.value]
              );
            }
          }}
        >
          {isSingleSelect ? (
            <>
              <HStack justifyContent="space-between" w="100%">
                <Typography
                  variant="bodyStrong"
                  color={
                    isNonAggregatable && isWarningDisplayed(option.value)
                      ? 'text.warning'
                      : selectedChoices?.includes(option.value)
                        ? 'text.selected'
                        : 'text.muted'
                  }
                >
                  {option.label}
                </Typography>
                {selectedChoices?.includes(option.value) &&
                  (isNonAggregatable && isWarningDisplayed(option.value) ? (
                    <CheckIcon color="text.warning" />
                  ) : (
                    <CheckIcon color="text.selected" />
                  ))}
              </HStack>
              {isNonAggregatable && isWarningDisplayed(option.value) && (
                <HStack justifyContent="left" w="100%" spacing={0}>
                  <Typography fontSize="12px" fontWeight={'400'} color={'text.warning'}>
                    Caution advised
                  </Typography>
                  <HelpTooltip
                    label={notAggregatableWarningText}
                    placement="bottom"
                    iconColor={'text.warning'}
                  />
                </HStack>
              )}
            </>
          ) : (
            <HStack width="100%" align="start">
              <Checkbox
                isChecked={selectedChoices?.includes(option.value) ?? false}
                pt="3px"
                pointerEvents="none"
              />
              <Typography variant="bodyStrong" color="text.muted">
                {option.label}
              </Typography>
            </HStack>
          )}
        </MenuItem>
      </div>
    );
  };
};

export const MenuSelector = ({
  options,
  isSingleSelect,
  data,
  handleChange,
  isTagMenuButton = false,
  menuWidth,
  additionalContent,
  isOptionDisabled = () => false,
  isNonAggregatable = false,
  isDisabled = false,
  notAggregatableWarningText,
  extra,
  isSearchable = false,
}: {
  options: { value: string; label: string }[];
  isSingleSelect: boolean;
  data: string[] | undefined;
  handleChange: (val: string[] | undefined) => void;
  isTagMenuButton?: boolean;
  menuWidth?: string;
  additionalContent?: React.ReactNode;
  isOptionDisabled?: (val: string) => boolean;
  isNonAggregatable?: boolean;
  isDisabled?: boolean;
  notAggregatableWarningText?: string;
  extra?: React.ReactNode;
  isSearchable?: boolean;
}) => {
  const [selectedChoices, setSelectedChoices] = useState<string[] | undefined>(data);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean | undefined>();
  const [searchValue, setSearchValue] = useState('');

  const choicesLabels = useMemo(
    () => data?.map((d) => options.find((opt) => opt.value === d)?.label ?? ''),
    [data]
  );

  const isWarningDisplayed = useMemo(
    () => (option: string) => showWarningForNotAggregatable(option),
    []
  );

  useEffect(() => setSelectedChoices(data), [data]);

  const filteredOptions: { value: string; label: string }[] = useMemo(() => {
    if (searchValue.trim() === '') {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.trim().toLowerCase())
    );
  }, [searchValue]);

  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 50,
        minHeight: 20,
      }),
    [filteredOptions]
  );

  const maxListHeight = 300;
  const estimatedRowHeight = 50;
  const listHeight = Math.min(filteredOptions.length * estimatedRowHeight, maxListHeight);
  const finalHeight = listHeight === 0 ? 50 : listHeight;

  return (
    <Menu matchWidth={false} isOpen={isMenuOpen} closeOnSelect={isSingleSelect ? undefined : false}>
      {isTagMenuButton ? (
        <MenuButton
          as={Tag}
          color={isNonAggregatable && isWarningDisplayed(selectedChoices![0]) ? 'text.warning' : ''}
          backgroundColor={
            isNonAggregatable && isWarningDisplayed(selectedChoices![0]) ? 'bg.warning' : ''
          }
          clickable
        >
          <ChoicesDisplay choices={choicesLabels} />
        </MenuButton>
      ) : (
        <MenuButton
          as={Button}
          variant={'ghost'}
          size="md"
          width="fit-content"
          textAlign="start"
          onClick={() => setIsMenuOpen(undefined)}
          pl="4px"
          isDisabled={isDisabled}
        >
          <ChoicesDisplay choices={choicesLabels} />
        </MenuButton>
      )}
      <MenuList padding="8px" w={menuWidth ?? 'unset'} overflow="auto">
        <VStack alignItems="stretch">
          {extra}
          {isSearchable && (
            <SearchInput
              width="100%"
              placeholder="Search"
              search={searchValue}
              setSearch={setSearchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          )}
          <Box height={`${finalHeight}px`}>
            <AutoSizer>
              {({ height, width }) => (
                <VirtualizedList
                  width={width}
                  height={height}
                  rowCount={filteredOptions.length}
                  rowHeight={cache.rowHeight}
                  deferredMeasurementCache={cache}
                  rowRenderer={({ index, key, parent, style }) => (
                    <CellMeasurer
                      cache={cache}
                      columnIndex={0}
                      key={key}
                      parent={parent}
                      rowIndex={index}
                    >
                      {({ registerChild }) => (
                        <div ref={registerChild} style={style}>
                          {MenuOptionsList({
                            options: filteredOptions,
                            isOptionDisabled,
                            isSingleSelect,
                            selectedChoices,
                            setSelectedChoices,
                            handleChange,
                            isNonAggregatable,
                            isWarningDisplayed,
                            notAggregatableWarningText,
                          })({ index, key, style: {} })}
                        </div>
                      )}
                    </CellMeasurer>
                  )}
                  overscanRowCount={5}
                />
              )}
            </AutoSizer>
          </Box>
          {!isSingleSelect && (
            <VStack gap="0px">
              <Divider color="border.decorative" mb="8px" ml="-8px" pr="16px" />
              <Box w="100%">
                <Button
                  variant="primary"
                  w="100%"
                  onClick={() => {
                    handleChange(selectedChoices);
                    if (!isSingleSelect) setIsMenuOpen(false);
                  }}
                >
                  Confirm
                </Button>
              </Box>
            </VStack>
          )}
          {additionalContent && (
            <>
              <Divider color="border.decorative" my="8px" ml="-8px" pr="16px" />
              {additionalContent}
            </>
          )}
        </VStack>
      </MenuList>
    </Menu>
  );
};
